import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  useCreateTeamBookmarkMutation,
  useDeleteTeamBookmarkMutation,
  useGetAllTeamBookmarksQuery,
} from "marketplace/bookmarks/_api/bookmark-api";
import { useSnackbar } from "notistack";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface TeamBookmarkButtonProps {
  className?: string;
  teamId?: Guid;
}

export default function TeamBookmarkButton(props: Readonly<TeamBookmarkButtonProps>) {
  const { className, teamId } = props;

  const { enqueueSnackbar } = useSnackbar();

  const { data: teamBookmarks, isLoading: bookmarksLoading } = useGetAllTeamBookmarksQuery();
  const [createTeamBookmark, { isLoading: isCreating }] = useCreateTeamBookmarkMutation();
  const [deleteTeamBookmark, { isLoading: isDeleting }] = useDeleteTeamBookmarkMutation();

  const existingBookmark = teamBookmarks?.find((b) => b.teamId === teamId?.value);
  const isLoading = bookmarksLoading || isCreating || isDeleting;

  const handleBookmarkClick = async () => {
    try {
      if (existingBookmark) {
        await deleteTeamBookmark({ bookmarkId: existingBookmark.id }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!teamId) throw new Error("Bookmark Team: Team ID is undefined");
        await createTeamBookmark({ teamId: teamId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  };

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={isLoading}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!teamId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
